import React, { useEffect, useState } from 'react';
import { authService, cityService, corporateUserService, districtService } from 'api';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { CorporateUserDetailsViewModel } from 'api/types/corporate';
import { CORPORATE_APPROVAL_STATUS_LABELS } from 'api/types/corporate';
import Button from 'components/Button';
import { useAuth } from 'contexts/AuthContext';

const emptyDetails = (): CorporateUserDetailsViewModel => ({
  companyName: '',
  taxNumber: '',
  phoneNumber: '',
  companyType: 'SoleProprietorship',
  taxOfficeCityId: 0,
  taxOfficeDistrictId: 0,
});

export default function CorporateProfileForm() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [taxCertificateFile, setTaxCertificateFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    companyName: '',
    companyType: 'SoleProprietorship' as 'SoleProprietorship' | 'LimitedOrAnonymous',
    taxNumber: '',
    nationalNumber: '',
    taxOfficeCityId: '',
    taxOfficeDistrictId: '',
    description: '',
  });
  const [readOnly, setReadOnly] = useState({
    companyAddress: '',
    companyActivityCode: '',
    companyTradeTitle: '',
    naceCode: '',
    taxCertificateImageUrl: '',
  });

  useEffect(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
  }, []);

  useEffect(() => {
    const cityId = form.taxOfficeCityId ? Number(form.taxOfficeCityId) : null;
    if (!cityId) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((r) => {
      if (r.isSuccess && r.data) setDistricts(r.data);
    });
  }, [form.taxOfficeCityId]);

  useEffect(() => {
    let cancelled = false;
    authService
      .getMe()
      .then((result) => {
        if (cancelled || !result.isSuccess || !result.data) return;
        const me = result.data;
        const d = me.corporateDetails ?? emptyDetails();
        setApprovalStatus(me.corporateApprovalStatus ?? null);
        setForm({
          firstName: me.firstName ?? '',
          lastName: me.lastName ?? '',
          phoneNumber: me.phoneNumber ?? d.phoneNumber ?? '',
          companyName: d.companyName ?? '',
          companyType: d.companyType ?? 'SoleProprietorship',
          taxNumber: d.taxNumber ?? '',
          nationalNumber: d.nationalNumber ?? '',
          taxOfficeCityId: d.taxOfficeCityId ? String(d.taxOfficeCityId) : '',
          taxOfficeDistrictId: d.taxOfficeDistrictId ? String(d.taxOfficeDistrictId) : '',
          description: d.description ?? me.description ?? '',
        });
        setReadOnly({
          companyAddress: d.companyAddress ?? '',
          companyActivityCode: d.companyActivityCode ?? '',
          companyTradeTitle: d.companyTradeTitle ?? '',
          naceCode: d.naceCode ?? '',
          taxCertificateImageUrl: d.taxCertificateImageUrl ?? '',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (form.companyType === 'SoleProprietorship' && !form.nationalNumber.trim()) {
        setError('Şahıs şirketi için T.C. kimlik numarası gereklidir.');
        return;
      }
      const result = await corporateUserService.updateMe(
        {
          firstName: form.firstName,
          lastName: form.lastName,
          phoneNumber: form.phoneNumber,
          companyName: form.companyName,
          companyType: form.companyType,
          taxNumber: form.taxNumber,
          nationalNumber: form.nationalNumber || null,
          taxOfficeCityId: Number(form.taxOfficeCityId),
          taxOfficeDistrictId: Number(form.taxOfficeDistrictId),
          description: form.description || null,
        },
        taxCertificateFile
      );
      if (result.isSuccess) {
        setSuccess('Profil güncellendi.');
        setTaxCertificateFile(null);
        await refreshUser();
      } else {
        setError(result.message ?? 'Profil güncellenemedi.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Profil güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-muted py-4">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Yükleniyor…
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
        <h5 className="card-title mb-0">
          <i className="fas fa-building text-primary me-2" />
          Kurumsal Profil
        </h5>
      </div>
      <div className="card-body">
        {approvalStatus && (
          <div className="alert alert-info py-2 small">
            Onay durumu: {CORPORATE_APPROVAL_STATUS_LABELS[approvalStatus as keyof typeof CORPORATE_APPROVAL_STATUS_LABELS] ?? approvalStatus}
          </div>
        )}
        {error && (
          <div className="alert alert-danger py-2 small" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success py-2 small" role="alert">
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Ad</label>
              <input
                className="form-control"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Soyad</label>
              <input
                className="form-control"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Marka adı</label>
            <input
              className="form-control"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              required
            />
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Telefon</label>
              <input
                className="form-control"
                value={form.phoneNumber}
                onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Vergi numarası</label>
              <input
                className="form-control"
                value={form.taxNumber}
                onChange={(e) => setForm((f) => ({ ...f, taxNumber: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Şirket türü</label>
            <select
              className="form-select"
              value={form.companyType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  companyType: e.target.value as 'SoleProprietorship' | 'LimitedOrAnonymous',
                }))
              }
            >
              <option value="SoleProprietorship">Şahıs şirketi</option>
              <option value="LimitedOrAnonymous">Limited / Anonim şirket</option>
            </select>
          </div>
          {form.companyType === 'SoleProprietorship' && (
            <div className="mb-3">
              <label className="form-label">T.C. Kimlik No</label>
              <input
                className="form-control"
                value={form.nationalNumber}
                onChange={(e) => setForm((f) => ({ ...f, nationalNumber: e.target.value }))}
                required
              />
            </div>
          )}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Vergi dairesi ili</label>
              <select
                className="form-select"
                value={form.taxOfficeCityId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    taxOfficeCityId: e.target.value,
                    taxOfficeDistrictId: '',
                  }))
                }
                required
              >
                <option value="">İl seçin</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Vergi dairesi ilçesi</label>
              <select
                className="form-select"
                value={form.taxOfficeDistrictId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, taxOfficeDistrictId: e.target.value }))
                }
                required
                disabled={!form.taxOfficeCityId}
              >
                <option value="">İlçe seçin</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Açıklama</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          {readOnly.taxCertificateImageUrl && (
            <div className="mb-3">
              <label className="form-label">Mevcut vergi levhası</label>
              <div>
                <img
                  alt="Vergi levhası"
                  className="img-fluid border rounded"
                  src={readOnly.taxCertificateImageUrl}
                  style={{ maxHeight: 200 }}
                />
              </div>
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Vergi levhasını güncelle (isteğe bağlı)</label>
            <input
              accept="image/jpeg,image/png,image/webp"
              className="form-control"
              type="file"
              onChange={(e) => setTaxCertificateFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {(readOnly.companyTradeTitle ||
            readOnly.companyAddress ||
            readOnly.companyActivityCode ||
            readOnly.naceCode) && (
            <div className="border rounded p-3 mb-3 bg-light">
              <h6 className="small text-muted mb-2">Yönetici tarafından girilen bilgiler (salt okunur)</h6>
              {readOnly.companyTradeTitle && (
                <p className="small mb-1">
                  <strong>Ticari unvan:</strong> {readOnly.companyTradeTitle}
                </p>
              )}
              {readOnly.companyAddress && (
                <p className="small mb-1">
                  <strong>Adres:</strong> {readOnly.companyAddress}
                </p>
              )}
              {readOnly.companyActivityCode && (
                <p className="small mb-1">
                  <strong>Faaliyet kodu:</strong> {readOnly.companyActivityCode}
                </p>
              )}
              {readOnly.naceCode && (
                <p className="small mb-0">
                  <strong>NACE:</strong> {readOnly.naceCode}
                </p>
              )}
            </div>
          )}
          <Button className="btn btn-primary" type="submit" disabled={saving} loading={saving}>
            Kaydet
          </Button>
        </form>
      </div>
    </div>
  );
}
